import { useState, useCallback } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Background } from '@Components/designConstants';
import { WidgetContainer } from '@Components/WidgetContainer';
import { AdOpsToolProps } from '../adops-constants';
import {
  fetchCometSelfInvoke,
  pollS3,
  StreamingUtilsLambdaFetch
} from '../../../utils/fetch-utils';

interface ProgramInfo {
  program: string;
  network: string;
  isci: string;
  rate: number;
  length: number;
}

interface SyndicationPostLogsInput {
  week: string;
  company: string;
  bvsPrefix: string;
  programInfos: [ProgramInfo];
}

interface SyndicationPostLogsResult {
  s3Key: string;
}

export const SyndicationPostLogs = ({ utils }: AdOpsToolProps) => {
  const { setError } = utils;
  const [activeAPICall, setActiveAPICall] = useState(false);
  const [week, setWeek] = useState('');
  const [company, setCompany] = useState('');
  const [bvsPrefix, setBvsPrefix] = useState('');
  const [program, setProgram] = useState('');
  const [network, setNetwork] = useState('');
  const [isci, setIsci] = useState('');
  const [rate, setRate] = useState('');
  const [length, setLength] = useState('');

  const generate = useCallback(async () => {
    try {
      setActiveAPICall(true);
      const programInfo: ProgramInfo = {
        program,
        network,
        isci,
        rate: Number(rate),
        length: Number(length)
      };
      const result = await fetchCometSelfInvoke<
        SyndicationPostLogsInput,
        SyndicationPostLogsResult
      >(StreamingUtilsLambdaFetch, '/createSyndicationPostLogs', {
        week,
        company,
        bvsPrefix,
        programInfos: [programInfo]
      });
      await pollS3({
        bucket: 'bpm-cache',
        filename: result.s3Key,
        mimeType: 'application/vnd.ms-excel',
        autoDownload: true,
        overloadFilename: `${company}-syndication-post-logs-${week}.xlsx`
      });
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
    setActiveAPICall(false);
  }, [week, company, bvsPrefix, program, network, isci, rate, length, setError]);

  return (
    <WidgetContainer
      className='linear-tool-card'
      header='Syndication Post Logs'
      background={Background.DARK}
      dataTestId='syndication-post-logs'
    >
      <div className='ad-ops-elem ad-ops-input-row'>
        <InputGroup>
          <InputGroup.Text className='input-label'>Week</InputGroup.Text>
          <Form.Control
            data-testid='syndication-week'
            value={week}
            onChange={e => setWeek(e.target.value)}
            placeholder='YYYY-MM-DD'
          />
        </InputGroup>
        <InputGroup>
          <InputGroup.Text className='input-label'>Company</InputGroup.Text>
          <Form.Control
            data-testid='syndication-company'
            value={company}
            onChange={e => setCompany(e.target.value)}
          />
        </InputGroup>
      </div>
      <div className='ad-ops-elem ad-ops-input-row'>
        <InputGroup>
          <InputGroup.Text className='input-label'>BVS Prefix</InputGroup.Text>
          <Form.Control value={bvsPrefix} onChange={e => setBvsPrefix(e.target.value)} />
        </InputGroup>
        <InputGroup>
          <InputGroup.Text className='input-label'>Program</InputGroup.Text>
          <Form.Control value={program} onChange={e => setProgram(e.target.value)} />
        </InputGroup>
      </div>
      <div className='ad-ops-elem ad-ops-input-row'>
        <InputGroup>
          <InputGroup.Text className='input-label'>Network</InputGroup.Text>
          <Form.Control value={network} onChange={e => setNetwork(e.target.value)} />
        </InputGroup>
        <InputGroup>
          <InputGroup.Text className='input-label'>ISCI</InputGroup.Text>
          <Form.Control value={isci} onChange={e => setIsci(e.target.value)} />
        </InputGroup>
      </div>
      <div className='ad-ops-elem ad-ops-input-row'>
        <InputGroup>
          <InputGroup.Text className='input-label'>Rate</InputGroup.Text>
          <Form.Control type='number' value={rate} onChange={e => setRate(e.target.value)} />
        </InputGroup>
        <InputGroup>
          <InputGroup.Text className='input-label'>Length</InputGroup.Text>
          <Form.Control type='number' value={length} onChange={e => setLength(e.target.value)} />
        </InputGroup>
      </div>
      <div className='ad-ops-elem'>
        <Button
          className='ad-ops-elem'
          data-testid='syndication-generate'
          loading={activeAPICall}
          onClick={generate}
        >
          Generate Post Logs
        </Button>
      </div>
    </WidgetContainer>
  );
};
